/**
 * Pacote 3 · P3-13 a P3-17 (D3 aprovada; mockups M5a–M5e):
 *  - "Reabrir para correção" 🔒: o que corrigir → impactos (aplicado na cadeia × pendente de ação externa) → antes e
 *    depois → confirmar. O servidor calcula a prévia e aplica tudo numa transação só (trava pela revisão do Pedido);
 *  - retificações aplicadas e pendências externas, com "Registrar conclusão" e "Registrar regularização" (M5d/M5e);
 *  - cancelar Pedido sem Ordem válida 🔒 (P3-17) e reabertura comercial 🔒 (só sem faturamento válido).
 * NF, lançamentos e pagamentos válidos nunca mudam; nada é enviado a terceiros pelo HUB.
 */
import { useState } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { hojeIso } from './CampoData.tsx';
import { Ajuda, Estado, EstadoCarga, Modal, useCarga } from './ui.tsx';
import { moeda } from './proposta/formatos.ts';
import { arquivoParaApi } from './Pedidos.tsx';

interface ParaCorrecao {
  numero: string;
  tipo: 'venda' | 'locacao' | 'trade_in';
  revisao: number;
  cancelado: boolean;
  proposta: { numero: string; versao: number };
  unidade: string;
  unidades: { id: string; razao: string; cnpj: string; codigo: string | null; cidade: string; uf: string }[];
  entidades: { id: string; nome: string; cnpj: string; grupo: string }[];
  itens: { itemId: string; nome: string; quantidade: number; precoUnit: number; custoUnit: number; entidadeId: string; entidade: string; faturado: number }[];
  locacao: { entidadeId: string; entidade: string; valorParcela: number; percentual: number; prazoMeses: number; componentes: { id: string; nome: string; quantidade: number }[] } | null;
  tradein: { entidadeId: string; entidade: string; modo: string; linhas: { id: string; nome: string; quantidade: number; unitBruto: number; unitCliente: number }[] } | null;
}
interface Previa {
  revisao: number;
  revisaoNova: number;
  bloqueios: string[];
  aplicado: string[];
  pendencias: { tipo: string; rotuloTipo: string; descricao: string }[];
  diferencas: { campo: string; antes: string; depois: string }[];
}
export interface PendenciaExterna {
  id: string;
  tipo: 'enviar_ordem' | 'comunicar_fornecedor' | 'regularizacao_fiscal' | 'aceite_cliente';
  rotuloTipo: string;
  descricao: string;
  ordem: string | null;
  nf: string | null;
  situacao: 'pendente' | 'concluida';
  conclusao: { data: string; autor: string; procedimento: string; documentoExterno: string; observacao: string; evidencia: { id: string; nome: string; oportunidadeId: string } | null } | null;
}
export interface RetificacaoAplicada {
  id: string;
  numero: string;
  revisao: number;
  registradaEm: string;
  autor: string;
  justificativa: string;
  aplicado: string[];
  diferencas: { campo: string; antes: string; depois: string }[];
  pendencias: PendenciaExterna[];
}

const PASSOS = ['O que corrigir', 'Impactos', 'Antes e depois', 'Confirmar'];
const num = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(/\./g, '').replace(',', '.')));
const txt = (v: number) => String(v).replace('.', ',');
const dataHoraBr = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}${s.length > 10 ? ` ${s.slice(11, 16)}` : ''}`;

function Passos({ atual }: { atual: number }) {
  return (
    <div className="passos-retificacao" data-teste="passos-retificacao">
      {PASSOS.map((p, i) => (
        <span key={p} className={`passo-retificacao${i + 1 === atual ? ' atual' : i + 1 < atual ? ' feito' : ''}`}>
          {i + 1 < atual ? '✓ ' : ''}
          {i + 1} · {p}
        </span>
      ))}
    </div>
  );
}

/** Rascunho editável: textos por campo (o servidor confere o formato). */
type Rascunho = {
  unidade: string;
  itens: Record<string, { quantidade: string; precoUnit: string; custoUnit: string; entidadeId: string }>;
  locacao: { entidadeId: string; valorParcela: string; percentual: string; componentes: Record<string, string> } | null;
  tradein: { entidadeId: string; linhas: Record<string, { quantidade: string; unitBruto: string; unitCliente: string }> } | null;
};
const rascunhoDe = (d: ParaCorrecao): Rascunho => ({
  unidade: d.unidade,
  itens: Object.fromEntries(d.itens.map((i) => [i.itemId, { quantidade: String(i.quantidade), precoUnit: txt(i.precoUnit), custoUnit: txt(i.custoUnit), entidadeId: i.entidadeId }])),
  locacao: d.locacao ? { entidadeId: d.locacao.entidadeId, valorParcela: txt(d.locacao.valorParcela), percentual: txt(d.locacao.percentual), componentes: Object.fromEntries(d.locacao.componentes.map((k) => [k.id, String(k.quantidade)])) } : null,
  tradein: d.tradein ? { entidadeId: d.tradein.entidadeId, linhas: Object.fromEntries(d.tradein.linhas.map((l) => [l.id, { quantidade: String(l.quantidade), unitBruto: txt(l.unitBruto), unitCliente: txt(l.unitCliente) }])) } : null,
});
const alteracoesDe = (r: Rascunho) => ({
  entidadeClienteId: r.unidade,
  itens: Object.entries(r.itens).map(([itemId, x]) => ({ itemId, quantidade: num(x.quantidade), precoUnit: num(x.precoUnit), custoUnit: num(x.custoUnit), entidadeId: x.entidadeId })),
  ...(r.locacao ? { locacao: { entidadeId: r.locacao.entidadeId, valorParcela: num(r.locacao.valorParcela), percentual: num(r.locacao.percentual), componentes: Object.entries(r.locacao.componentes).map(([id, q]) => ({ id, quantidade: num(q) })) } } : {}),
  ...(r.tradein ? { tradein: { entidadeId: r.tradein.entidadeId, linhas: Object.entries(r.tradein.linhas).map(([id, x]) => ({ id, quantidade: num(x.quantidade), unitBruto: num(x.unitBruto), unitCliente: num(x.unitCliente) })) } } : {}),
});

function Era({ mudou, texto }: { mudou: boolean; texto: string }) {
  return mudou ? <small className="era-retificacao">era {texto}</small> : null;
}

function CamposCorrecao({ d, r, setR }: { d: ParaCorrecao; r: Rascunho; setR: (x: Rascunho) => void }) {
  const opcoesEntidade = (atual: string) => (
    <>
      {!d.entidades.some((e) => e.id === atual) && <option value={atual}>(entidade atual, inativa)</option>}
      {d.entidades.map((e) => (
        <option key={e.id} value={e.id}>
          {e.nome} — {e.cnpj}
        </option>
      ))}
    </>
  );
  const nomeEntidade = (id: string) => d.entidades.find((e) => e.id === id)?.nome ?? '—';
  const unidadeAtual = d.unidades.find((u) => u.id === d.unidade);
  return (
    <>
      <div className="bloco-unidade" data-teste="unidade-correcao">
        <label className="cab-unidade" htmlFor="unidade-correcao">
          Filial/unidade do Cliente
        </label>
        <select id="unidade-correcao" value={r.unidade} onChange={(e) => setR({ ...r, unidade: e.target.value })} data-teste="unidade-retificacao">
          {d.unidades.map((u) => (
            <option key={u.id} value={u.id}>
              {u.razao} — {u.cnpj}
              {u.codigo ? ` · ${u.codigo}` : ''}
            </option>
          ))}
        </select>
        <Era mudou={r.unidade !== d.unidade} texto={unidadeAtual ? `${unidadeAtual.razao} — ${unidadeAtual.cnpj}` : ''} />
      </div>
      {d.tipo === 'venda' && (
        <div className="rolagem">
          <table className="tabela tabela-correcao" data-teste="itens-correcao">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qtd</th>
                <th>Custo unit. (R$)</th>
                <th>Venda unit. (R$)</th>
                <th>Entidade faturadora</th>
              </tr>
            </thead>
            <tbody>
              {d.itens.map((i) => {
                const x = r.itens[i.itemId];
                const set = (k: keyof typeof x, v: string) => setR({ ...r, itens: { ...r.itens, [i.itemId]: { ...x, [k]: v } } });
                return (
                  <tr key={i.itemId}>
                    <td>
                      {i.nome}
                      {i.faturado > 0 && <small className="info-secundaria bloco">{i.faturado} faturada(s): não reduz abaixo disso</small>}
                    </td>
                    <td>
                      <input className="campo-numero-curto" inputMode="numeric" value={x.quantidade} onChange={(e) => set('quantidade', e.target.value)} aria-label={`Quantidade de ${i.nome}`} data-teste={`qtd-${i.itemId}`} />
                      <Era mudou={num(x.quantidade) !== i.quantidade} texto={String(i.quantidade)} />
                    </td>
                    <td>
                      <input className="campo-numero-curto" inputMode="decimal" value={x.custoUnit} onChange={(e) => set('custoUnit', e.target.value)} aria-label={`Custo unitário de ${i.nome}`} data-teste={`custo-${i.itemId}`} />
                      <Era mudou={num(x.custoUnit) !== i.custoUnit} texto={moeda(i.custoUnit)} />
                    </td>
                    <td>
                      <input className="campo-numero-curto" inputMode="decimal" value={x.precoUnit} onChange={(e) => set('precoUnit', e.target.value)} aria-label={`Venda unitária de ${i.nome}`} data-teste={`preco-${i.itemId}`} />
                      <Era mudou={num(x.precoUnit) !== i.precoUnit} texto={moeda(i.precoUnit)} />
                    </td>
                    <td>
                      <select value={x.entidadeId} onChange={(e) => set('entidadeId', e.target.value)} aria-label={`Entidade faturadora de ${i.nome}`} data-teste={`entidade-${i.itemId}`}>
                        {opcoesEntidade(i.entidadeId)}
                      </select>
                      <Era mudou={x.entidadeId !== i.entidadeId} texto={i.entidade || nomeEntidade(i.entidadeId)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {d.locacao && r.locacao && (
        <div className="grade-3" data-teste="locacao-correcao">
          <label className="campo">
            Parceiro de locação
            <select value={r.locacao.entidadeId} onChange={(e) => setR({ ...r, locacao: { ...r.locacao!, entidadeId: e.target.value } })} data-teste="entidade-locacao">
              {opcoesEntidade(d.locacao.entidadeId)}
            </select>
            <Era mudou={r.locacao.entidadeId !== d.locacao.entidadeId} texto={d.locacao.entidade} />
          </label>
          <label className="campo">
            Valor da parcela (R$)
            <input inputMode="decimal" value={r.locacao.valorParcela} onChange={(e) => setR({ ...r, locacao: { ...r.locacao!, valorParcela: e.target.value } })} data-teste="valor-parcela" />
            <Era mudou={num(r.locacao.valorParcela) !== d.locacao.valorParcela} texto={moeda(d.locacao.valorParcela)} />
          </label>
          <label className="campo">
            % Margem
            <input inputMode="decimal" value={r.locacao.percentual} onChange={(e) => setR({ ...r, locacao: { ...r.locacao!, percentual: e.target.value } })} data-teste="percentual-locacao" />
            <Era mudou={num(r.locacao.percentual) !== d.locacao.percentual} texto={`${txt(d.locacao.percentual)}%`} />
          </label>
          {d.locacao.componentes.map((k) => (
            <label key={k.id} className="campo">
              {k.nome} · quantidade
              <input inputMode="numeric" value={r.locacao!.componentes[k.id]} onChange={(e) => setR({ ...r, locacao: { ...r.locacao!, componentes: { ...r.locacao!.componentes, [k.id]: e.target.value } } })} />
              <Era mudou={num(r.locacao!.componentes[k.id]) !== k.quantidade} texto={String(k.quantidade)} />
            </label>
          ))}
        </div>
      )}
      {d.tradein && r.tradein && (
        <>
          <label className="campo">
            Comprador
            <select value={r.tradein.entidadeId} onChange={(e) => setR({ ...r, tradein: { ...r.tradein!, entidadeId: e.target.value } })} data-teste="entidade-tradein">
              {opcoesEntidade(d.tradein.entidadeId)}
            </select>
            <Era mudou={r.tradein.entidadeId !== d.tradein.entidadeId} texto={d.tradein.entidade} />
          </label>
          <div className="rolagem">
            <table className="tabela tabela-correcao">
              <thead>
                <tr>
                  <th>Equipamento</th>
                  <th>Qtd</th>
                  <th>Avaliação unit. (R$)</th>
                  {d.tradein.modo === 'valor' && <th>Valor ao Cliente unit. (R$)</th>}
                </tr>
              </thead>
              <tbody>
                {d.tradein.linhas.map((l) => {
                  const x = r.tradein!.linhas[l.id];
                  const set = (k: keyof typeof x, v: string) => setR({ ...r, tradein: { ...r.tradein!, linhas: { ...r.tradein!.linhas, [l.id]: { ...x, [k]: v } } } });
                  return (
                    <tr key={l.id}>
                      <td>{l.nome}</td>
                      <td>
                        <input className="campo-numero-curto" inputMode="numeric" value={x.quantidade} onChange={(e) => set('quantidade', e.target.value)} />
                        <Era mudou={num(x.quantidade) !== l.quantidade} texto={String(l.quantidade)} />
                      </td>
                      <td>
                        <input className="campo-numero-curto" inputMode="decimal" value={x.unitBruto} onChange={(e) => set('unitBruto', e.target.value)} />
                        <Era mudou={num(x.unitBruto) !== l.unitBruto} texto={moeda(l.unitBruto)} />
                      </td>
                      {d.tradein!.modo === 'valor' && (
                        <td>
                          <input className="campo-numero-curto" inputMode="decimal" value={x.unitCliente} onChange={(e) => set('unitCliente', e.target.value)} />
                          <Era mudou={num(x.unitCliente) !== l.unitCliente} texto={moeda(l.unitCliente)} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

function ListaImpactos({ previa }: { previa: Previa }) {
  return (
    <div className="duas-colunas-impacto" data-teste="impactos-retificacao">
      <div className="bloco-impacto">
        <h3>
          <span className="selo-previa ok">Será aplicado na cadeia</span>
        </h3>
        <ul className="lista-aplicado">
          {previa.aplicado.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      </div>
      <div className="bloco-impacto">
        <h3>
          <span className="selo-previa ext">Continuará pendente de ação externa</span>
          <Ajuda titulo="Pendência de ação externa">O HUB não envia nada a Cliente, fornecedor ou locadora e não altera NF nem pagamento válido. Cada pendência tem o seu registro de conclusão, com data e evidência.</Ajuda>
        </h3>
        {previa.pendencias.length ? (
          <ul>
            {previa.pendencias.map((p) => (
              <li key={p.descricao}>{p.tipo === 'regularizacao_fiscal' ? <b>{p.descricao}</b> : p.descricao}</li>
            ))}
          </ul>
        ) : (
          <p className="texto-secundario sem-margem">Nenhuma.</p>
        )}
        {previa.pendencias.some((p) => p.tipo === 'regularizacao_fiscal') && <p className="texto-secundario sem-margem">A NF continua válida no HUB: não é alterada nem estornada.</p>}
      </div>
    </div>
  );
}

export function TabelaDiferencas({ diferencas, revisao, teste = 'antes-depois' }: { diferencas: { campo: string; antes: string; depois: string }[]; revisao: number; teste?: string }) {
  return (
    <div className="rolagem">
      <table className="tabela tabela-diferencas" data-teste={teste}>
        <thead>
          <tr>
            <th>Campo</th>
            <th>Antes (r{revisao - 1})</th>
            <th>Depois (r{revisao})</th>
          </tr>
        </thead>
        <tbody>
          {diferencas.map((x) => (
            <tr key={x.campo}>
              <td>{x.campo}</td>
              <td className="antes">{x.antes}</td>
              <td className="depois">{x.depois}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ModalReabrirCorrecao({ numero, aoFechar, aoAplicar }: { numero: string; aoFechar: () => void; aoAplicar: () => void }) {
  const carga = useCarga(() => api.get<ParaCorrecao>(`/api/pedidos/${numero}/correcao`), [numero]);
  const [r, setR] = useState<Rascunho | null>(null);
  const [passo, setPasso] = useState(1);
  const [justificativa, setJustificativa] = useState('');
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const d = carga.dados;
  const rasc = r ?? (d ? rascunhoDe(d) : null);
  const verImpactos = async () => {
    if (!rasc || ocupado) return;
    if (justificativa.trim().length < 4) {
      setErro('Informe a justificativa da correção (mínimo de 4 caracteres).');
      return;
    }
    setOcupado(true);
    setErro('');
    try {
      const p = await api.post<Previa>(`/api/pedidos/${numero}/correcao/previa`, { alteracoes: alteracoesDe(rasc) });
      setPrevia(p);
      setPasso(2);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  const confirmar = async () => {
    if (!rasc || !d || ocupado) return;
    setOcupado(true);
    setErro('');
    try {
      await api.post(`/api/pedidos/${numero}/correcao`, { revisao: d.revisao, alteracoes: alteracoesDe(rasc), justificativa: justificativa.trim() });
      setPasso(4);
      aoAplicar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setOcupado(false);
    }
  };
  const bloqueado = Boolean(previa?.bloqueios.length);
  const rodape =
    passo === 1 ? (
      <>
        <button type="button" className="botao-secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button type="button" className="botao-primario" disabled={!d || ocupado} aria-busy={ocupado} onClick={verImpactos} data-teste="ver-impactos">
          Ver impactos
        </button>
      </>
    ) : passo === 2 ? (
      <>
        <button type="button" className="botao-secundario" onClick={() => setPasso(1)}>
          Voltar
        </button>
        <button type="button" className="botao-primario" disabled={bloqueado} onClick={() => setPasso(3)} data-teste="ver-antes-depois">
          Ver antes e depois
        </button>
      </>
    ) : passo === 3 ? (
      <>
        <button type="button" className="botao-secundario" onClick={() => setPasso(2)}>
          Voltar
        </button>
        <button type="button" className="botao-primario" disabled={bloqueado || ocupado} aria-busy={ocupado} onClick={confirmar} data-teste="confirmar-retificacao">
          Confirmar retificação
        </button>
      </>
    ) : (
      <button type="button" className="botao-primario" onClick={aoFechar} data-teste="fechar-retificacao">
        Fechar
      </button>
    );
  return (
    <Modal titulo={`Reabrir para correção · ${numero}`} aoFechar={aoFechar} largo rodape={rodape}>
      <Passos atual={passo} />
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-retificacao">
          {erro}
        </p>
      )}
      {!d || !rasc ? (
        <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="correcao" />
      ) : passo === 1 ? (
        <>
          <p className="texto-secundario sem-margem">
            O resultado comercial continua o mesmo. A Proposta {d.proposta.numero} v{d.proposta.versao} aceita fica como está; a correção gera uma versão que a retifica e a revisão r{d.revisao + 1} do {d.numero}.
            <Ajuda titulo="Reabrir para correção">Corrige valores, quantidades, parceiro e filial/unidade em qualquer estado, inclusive depois da emissão ou do faturamento. NF e pagamentos válidos nunca mudam: o que depende de terceiros vira pendência de ação externa.</Ajuda>
          </p>
          <CamposCorrecao d={d} r={rasc} setR={setR} />
          <label className="campo">
            Justificativa *
            <textarea rows={2} maxLength={1000} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="justificativa-retificacao" />
          </label>
        </>
      ) : passo === 2 && previa ? (
        <>
          {bloqueado && <Estado tipo="bloqueado" titulo="A correção não pode ser aplicada assim" itens={previa.bloqueios} teste="bloqueios-retificacao" />}
          <ListaImpactos previa={previa} />
        </>
      ) : passo === 3 && previa ? (
        <TabelaDiferencas diferencas={previa.diferencas} revisao={previa.revisaoNova} />
      ) : (
        <Estado tipo="concluido" titulo={`Retificação aplicada: ${d.numero} na revisão r${d.revisao + 1}`} teste="retificacao-aplicada">
          As pendências de ação externa aparecem no Pedido, em “Retificações”.
        </Estado>
      )}
    </Modal>
  );
}

/* ================= pendências externas (M5d/M5e) ================= */

const PROCEDIMENTOS = ['Carta de correção', 'NF substituta', 'NF complementar', 'Devolução', 'Outro'];

export function ModalConcluirPendencia({ p, aoFechar, aoSalvar }: { p: PendenciaExterna; aoFechar: () => void; aoSalvar: () => void }) {
  const fiscal = p.tipo === 'regularizacao_fiscal';
  const [data, setData] = useState(hojeIso());
  const [procedimento, setProcedimento] = useState('');
  const [documento, setDocumento] = useState('');
  const [observacao, setObservacao] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const exigeEvidencia = fiscal || p.tipo === 'aceite_cliente';
  const salvar = async () => {
    if (salvando) return;
    const faltam = [fiscal && !procedimento && 'Escolha o procedimento adotado.', fiscal && procedimento === 'Outro' && !observacao.trim() && 'Procedimento “Outro”: descreva-o na Observação.', exigeEvidencia && !arquivo && 'Anexe a evidência.'].filter(Boolean) as string[];
    if (faltam.length) {
      setErro(faltam.join(' '));
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await api.post(`/api/pendencias-externas/${p.id}/concluir`, { data, procedimento, documento_externo: documento, observacao, ...(arquivo ? { evidencia: await arquivoParaApi(arquivo) } : {}) });
      aoSalvar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={fiscal ? `Registrar regularização fiscal externa${p.nf ? ` · NF ${p.nf}` : ''}` : 'Registrar conclusão'}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" disabled={salvando} aria-busy={salvando} onClick={salvar} data-teste="registrar-conclusao">
            Registrar
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-conclusao-pendencia">
          {erro}
        </p>
      )}
      <p className="descricao-pendencia">{p.descricao}</p>
      <div className="grade-2">
        <label className="campo">
          Data da conclusão *
          <input type="date" value={data} max={hojeIso()} onChange={(e) => setData(e.target.value)} data-teste="data-conclusao" />
        </label>
        {fiscal ? (
          <label className="campo">
            <span>
              Procedimento adotado *<Ajuda titulo="Procedimento adotado">O procedimento informado pelo emissor da NF. Se ele substituir a NF, o estorno e o novo lançamento são feitos por você na Ordem.</Ajuda>
            </span>
            <select value={procedimento} onChange={(e) => setProcedimento(e.target.value)} data-teste="procedimento">
              <option value="">Escolha o procedimento informado pelo emissor</option>
              {PROCEDIMENTOS.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
        ) : (
          <label className="campo">
            Como foi feito
            <input value={procedimento} maxLength={200} onChange={(e) => setProcedimento(e.target.value)} placeholder="Ex.: e-mail ao fornecedor" data-teste="procedimento" />
          </label>
        )}
        <label className="campo">
          Documento externo
          <input value={documento} maxLength={200} onChange={(e) => setDocumento(e.target.value)} placeholder="Número e data" data-teste="documento-externo" />
        </label>
        <label className="campo">
          Evidência {exigeEvidencia ? '*' : ''}
          <input type="file" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} data-teste="evidencia" />
        </label>
      </div>
      <label className="campo">
        Observação
        <textarea rows={2} maxLength={1000} value={observacao} onChange={(e) => setObservacao(e.target.value)} data-teste="observacao-conclusao" />
      </label>
      <p className="texto-secundario sem-margem">A evidência fica nos Documentos da Oportunidade, ligada a esta pendência.</p>
    </Modal>
  );
}

export function TabelaPendencias({ pendencias, podeConcluir, aoMudar, teste = 'pendencias-externas' }: { pendencias: PendenciaExterna[]; podeConcluir: boolean; aoMudar: () => void; teste?: string }) {
  const [aberta, setAberta] = useState<PendenciaExterna | null>(null);
  if (!pendencias.length) return <p className="texto-secundario sem-margem">Nenhuma pendência de ação externa.</p>;
  return (
    <div className="rolagem">
      <table className="tabela" data-teste={teste}>
        <thead>
          <tr>
            <th>Pendência</th>
            <th>Situação</th>
            <th>Registrar a conclusão</th>
          </tr>
        </thead>
        <tbody>
          {pendencias.map((p) => (
            <tr key={p.id} data-teste={`pendencia-${p.tipo}`}>
              <td>{p.tipo === 'regularizacao_fiscal' ? <b>{p.descricao}</b> : p.descricao}</td>
              <td>{p.situacao === 'concluida' ? <span className="selo-previa ok">Concluída em {dataHoraBr(p.conclusao!.data)}</span> : <span className="selo-previa pend">Pendente</span>}</td>
              <td>
                {p.conclusao ? (
                  <span className="info-secundaria">
                    {p.conclusao.procedimento || 'Registrada'} · {p.conclusao.autor}
                    {p.conclusao.documentoExterno ? ` · ${p.conclusao.documentoExterno}` : ''}
                    {p.conclusao.evidencia ? (
                      <>
                        {' · '}
                        <a href={`#/oportunidades/${p.conclusao.evidencia.oportunidadeId}`}>evidência</a>
                      </>
                    ) : null}
                  </span>
                ) : podeConcluir ? (
                  <button type="button" className="botao-secundario pequeno" onClick={() => setAberta(p)} data-teste={`concluir-${p.tipo}`}>
                    {p.tipo === 'regularizacao_fiscal' ? 'Registrar regularização' : 'Registrar conclusão'}
                  </button>
                ) : (
                  <span className="info-secundaria">Sem permissão</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {aberta && (
        <ModalConcluirPendencia
          p={aberta}
          aoFechar={() => setAberta(null)}
          aoSalvar={() => {
            setAberta(null);
            aoMudar();
          }}
        />
      )}
    </div>
  );
}

export function RetificacoesDoPedido({ retificacoes, aoMudar }: { retificacoes: RetificacaoAplicada[]; aoMudar: () => void }) {
  const { pode } = useSessao();
  if (!retificacoes.length) return null;
  return (
    <section className="secao-retificacoes" data-teste="retificacoes-pedido">
      <h2>
        Retificações<Ajuda titulo="Retificações">Cada retificação mostra o que foi aplicado na cadeia e as pendências de ação externa. Versões anteriores, NFs e pagamentos válidos ficam preservados.</Ajuda>
      </h2>
      {retificacoes.map((r) => (
        <div key={r.id} className="bloco-impacto" data-teste={`retificacao-${r.numero}`}>
          <h3>
            Retificação {r.numero} · revisão r{r.revisao} · {dataHoraBr(r.registradaEm)} · {r.autor}
          </h3>
          <p className="sem-margem">Justificativa: {r.justificativa}</p>
          <div className="secao-previa">
            <span className="selo-previa ok">Aplicado na cadeia</span>
            <ul className="lista-aplicado">
              {r.aplicado.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
          {r.diferencas.length > 0 && (
            <details className="acordeao-cotacoes">
              <summary>Antes e depois</summary>
              <TabelaDiferencas diferencas={r.diferencas} revisao={r.revisao} teste={`antes-depois-${r.numero}`} />
            </details>
          )}
          <div className="secao-previa">
            <span className="selo-previa ext">Pendências de ação externa</span>
            <TabelaPendencias pendencias={r.pendencias} podeConcluir={pode('reabrir_correcao', 'pedido')} aoMudar={aoMudar} teste={`pendencias-${r.numero}`} />
          </div>
        </div>
      ))}
    </section>
  );
}

/* ================= P3-17: cancelar Pedido sem Ordem ================= */

export function ModalCancelarPedido({ numero, aoFechar, aoCancelar }: { numero: string; aoFechar: () => void; aoCancelar: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    if (salvando) return;
    if (motivo.trim().length < 4) {
      setErro('Informe o motivo do cancelamento (mínimo de 4 caracteres).');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await api.post(`/api/pedidos/${numero}/cancelar`, { motivo: motivo.trim(), observacao: observacao.trim() });
      aoCancelar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Cancelar o ${numero}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={salvando} aria-busy={salvando} onClick={salvar} data-teste="confirmar-cancelar-pedido">
            Cancelar o Pedido
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-cancelar-pedido">
          {erro}
        </p>
      )}
      <p className="sem-margem">Só é possível sem Ordem válida. O cancelamento é definitivo, fica no histórico e o Pedido passa a “Cancelado” (sem contagem dupla nos indicadores).</p>
      <label className="campo">
        Motivo *
        <input value={motivo} maxLength={200} onChange={(e) => setMotivo(e.target.value)} data-teste="motivo-cancelar-pedido" />
      </label>
      <label className="campo">
        Observação
        <textarea rows={2} maxLength={1000} value={observacao} onChange={(e) => setObservacao(e.target.value)} />
      </label>
    </Modal>
  );
}

/* ================= reabertura comercial ================= */

interface PreviaReabertura {
  permitida: boolean;
  bloqueios: string[];
  efeitos: string[];
  pendencias: { tipo: string; rotuloTipo: string; descricao: string }[];
}

export function ModalReaberturaComercial({ oportunidadeId, aoFechar, aoReabrir }: { oportunidadeId: string; aoFechar: () => void; aoReabrir: () => void }) {
  const previa = useCarga(() => api.get<PreviaReabertura>(`/api/oportunidades/${oportunidadeId}/reabertura`), [oportunidadeId]);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    if (salvando) return;
    if (justificativa.trim().length < 4) {
      setErro('Informe a justificativa da reabertura (mínimo de 4 caracteres).');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await api.post(`/api/oportunidades/${oportunidadeId}/reabertura`, { justificativa: justificativa.trim() });
      aoReabrir();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  const p = previa.dados;
  return (
    <Modal
      titulo="Reabertura comercial"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" disabled={!p?.permitida || salvando} aria-busy={salvando} onClick={salvar} data-teste="confirmar-reabertura">
            Reabrir a negociação
          </button>
        </>
      }
    >
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-reabertura">
          {erro}
        </p>
      )}
      {!p ? (
        <EstadoCarga carregando={previa.carregando} erro={previa.erro} recarregar={previa.recarregar} teste="previa-reabertura" />
      ) : !p.permitida ? (
        <Estado tipo="bloqueado" titulo="Reabertura comercial não permitida" itens={p.bloqueios} teste="reabertura-bloqueada" />
      ) : (
        <>
          <p className="sem-margem">
            Retoma a negociação e revoga o resultado.<Ajuda titulo="Reabertura comercial">Diferente de “Reabrir para correção”: a Oportunidade volta a Negociação, o Pedido e as Ordens sem faturamento são cancelados (motivo “Reabertura”) e um novo resultado gera um novo Pedido.</Ajuda>
          </p>
          <div className="duas-colunas-impacto" data-teste="previa-reabertura">
            <div className="bloco-impacto">
              <h3>
                <span className="selo-previa ok">Será aplicado</span>
              </h3>
              <ul className="lista-aplicado">
                {p.efeitos.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
            <div className="bloco-impacto">
              <h3>
                <span className="selo-previa ext">Pendente de ação externa</span>
              </h3>
              {p.pendencias.length ? (
                <ul>
                  {p.pendencias.map((x) => (
                    <li key={x.descricao}>{x.descricao}</li>
                  ))}
                </ul>
              ) : (
                <p className="texto-secundario sem-margem">Nenhuma.</p>
              )}
            </div>
          </div>
          <label className="campo">
            Justificativa *
            <textarea rows={2} maxLength={1000} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="justificativa-reabertura" />
          </label>
        </>
      )}
    </Modal>
  );
}

/** Reaberturas comerciais da Oportunidade, com as pendências externas. */
export function ReaberturasDaOportunidade({ oportunidadeId }: { oportunidadeId: string }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<{ id: string; registradaEm: string; autor: string; justificativa: string; pendencias: PendenciaExterna[] }[]>(`/api/oportunidades/${oportunidadeId}/reaberturas`), [oportunidadeId]);
  if (!carga.dados?.length) return null;
  return (
    <section className="secao-retificacoes" data-teste="reaberturas-oportunidade">
      <h2>Reaberturas comerciais</h2>
      {carga.dados.map((r) => (
        <div key={r.id} className="bloco-impacto">
          <h3>
            {dataHoraBr(r.registradaEm)} · {r.autor}
          </h3>
          <p className="sem-margem">Justificativa: {r.justificativa}</p>
          <TabelaPendencias pendencias={r.pendencias} podeConcluir={pode('reabrir_comercial', 'oportunidade')} aoMudar={() => void carga.recarregar()} teste="pendencias-reabertura" />
        </div>
      ))}
    </section>
  );
}
